import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, CheckSquare, Clapperboard, Plus, Video } from 'lucide-react';
import type { CalendarItem } from '@unitly/contracts';
import { useCalendar, useConnectors } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { PageHeader } from '../shell/Chrome.tsx';
import { MonthGrid, MonthNav, type GridItem } from '../../ui/MonthGrid.tsx';
import { monthOf, monthViewRange } from '../../lib/calendar.ts';
import { diffDays, longDate } from '../../lib/dates.ts';
import { Agenda } from './Agenda.tsx';
import { EventSheet } from './EventSheet.tsx';

type Filter = 'event' | 'task' | 'content';
const FILTERS: { id: Filter; label: string; icon: React.ReactNode }[] = [
  { id: 'event', label: 'Eventi', icon: <CalendarDays size={15} /> },
  { id: 'content', label: 'PED', icon: <Clapperboard size={15} /> },
  { id: 'task', label: 'Task', icon: <CheckSquare size={15} /> },
];

const readFilters = (): Filter[] => {
  try { const v = JSON.parse(localStorage.getItem('unitly-cal-filters') ?? 'null'); return Array.isArray(v) ? v : ['event', 'content', 'task']; } catch { return ['event', 'content', 'task']; }
};

export function CalendarPage() {
  const { orgId, today, base, openTask, openContent } = useOrgCtx();
  const [month, setMonth] = useState(() => monthOf(today));
  const [selected, setSelected] = useState<string>(today);
  const [filters, setFiltersState] = useState<Filter[]>(readFilters);
  const [eventSheet, setEventSheet] = useState<{ item?: CalendarItem; date?: string } | null>(null);
  const setFilters = (f: Filter[]) => { setFiltersState(f); try { localStorage.setItem('unitly-cal-filters', JSON.stringify(f)); } catch { /* ignore */ } };
  const range = monthViewRange(month);
  const calendar = useCalendar(orgId, range.from, range.to);
  const connectors = useConnectors(orgId);
  const google = connectors.data?.find((c) => c.key === 'google_calendar');

  const items = useMemo(() => (calendar.data ?? []).filter((i) => filters.includes(i.kind)), [calendar.data, filters]);
  const open = (i: CalendarItem) => (i.kind === 'task' ? openTask(i.refId) : i.kind === 'content' ? openContent(i.refId) : setEventSheet({ item: i }));

  // Multi-day events appear on every day they cover
  const gridItems: GridItem[] = items.flatMap((i) => {
    const days: string[] = [];
    for (let d = i.startDate; d <= i.endDate && days.length < 31; d = new Date(Date.parse(`${d}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10)) days.push(d);
    return days.map((d, n) => ({ id: n ? `${i.id}#${n}` : i.id, date: d, title: i.title, time: n ? null : i.startTime, color: i.color, done: i.done }));
  });
  const byId = new Map(items.map((i) => [i.id, i]));
  const dayItems = items.filter((i) => i.startDate <= selected && i.endDate >= selected);
  const icon = (i: CalendarItem) => (i.kind === 'task' ? <CheckSquare size={16} /> : i.kind === 'content' ? <Clapperboard size={16} /> : i.conferenceUrl ? <Video size={16} /> : <CalendarDays size={16} />);

  return (
    <div className="page wide">
      <PageHeader
        title="Calendario"
        actions={<button type="button" className="btn btn-primary btn-sm" onClick={() => setEventSheet({ date: selected })}><Plus size={16} /> Evento</button>}
        mobileActions={<button type="button" className="icon-btn" aria-label="Nuovo evento" onClick={() => setEventSheet({ date: selected })}><Plus size={22} /></button>}
      />

      <div className="toolbar">
        <MonthNav month={month} onChange={(m) => { setMonth(m); setSelected(m === monthOf(today) ? today : `${m}-01`); }} today={today} />
        <span className="spacer" />
        <div className="people-picker" role="group" aria-label="Mostra">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className="channel-chip" aria-pressed={filters.includes(f.id)}
              onClick={() => setFilters(filters.includes(f.id) ? filters.filter((x) => x !== f.id) : [...filters, f.id])}>
              {f.icon} {f.label}
            </button>
          ))}
        </div>
      </div>

      {google?.configured && google.connections.length === 0 && (
        <Link to={`${base}/settings/integrations`} className="card list-row" style={{ marginBottom: 14 }}>
          <span className="connector-logo" style={{ width: 36, height: 36 }}><GoogleCalendarLogo size={22} /></span>
          <span className="grow"><div style={{ fontWeight: 600 }}>Collega Google Calendar</div><div className="secondary">Vedi qui i tuoi meeting insieme a task e PED.</div></span>
        </Link>
      )}

      <MonthGrid
        month={month} today={today} selected={selected} onSelect={setSelected} items={gridItems}
        onItemClick={(it) => { const i = byId.get(it.id.split('#')[0]!); if (i) open(i); }}
        onAdd={(date) => setEventSheet({ date })}
      />

      <Agenda
        today={today}
        emptyText={`Niente in programma ${diffDays(selected, today) === 0 ? 'oggi' : `il ${longDate(selected).toLowerCase()}`}.`}
        onAddDay={(d) => setEventSheet({ date: d })}
        entries={dayItems.map((i) => ({
          id: i.id, date: selected, time: i.startDate === selected ? i.startTime : null, title: i.title, color: i.color, done: i.done,
          icon: icon(i),
          sub: <>
            {i.endTime && i.startDate === selected && <span>fino alle {i.endTime}</span>}
            {i.workspaceName && <span>{i.workspaceName}</span>}
            {i.calendarName && <span>Google · {i.calendarName}</span>}
            {i.location && <span>{i.location}</span>}
          </>,
          onOpen: () => open(i),
        }))}
      />

      <EventSheet state={eventSheet} onClose={() => setEventSheet(null)} />
    </div>
  );
}

export function GoogleCalendarLogo({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <rect x="6" y="6" width="36" height="36" rx="6" fill="#fff" stroke="#DADCE0" />
      <path d="M6 14a8 8 0 0 1 8-8h20a8 8 0 0 1 8 8v2H6z" fill="#1A73E8" />
      <text x="24" y="37" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="16" fill="#1A73E8">31</text>
    </svg>
  );
}
